<template>
  <div class="fixed inset-0 z-50" role="dialog" aria-modal="true">
    <div class="absolute inset-0 bg-gray-800 bg-opacity-60" />

    <div class="absolute inset-0 overflow-y-auto">
      <div class="min-h-full flex items-center justify-center p-4 sm:p-6">
        <div class="w-full max-w-xl rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-5">
          <div class="p-6 sm:p-8">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-xl sm:text-2xl font-semibold text-gray-900">What brought you to Kairo?</h2>
                <p class="mt-1 text-sm text-gray-600">Pick one — you can change this later.</p>
              </div>
            </div>

            <div class="mt-6 space-y-3">
              <button
                type="button"
                class="w-full text-left rounded-lg border p-4 transition"
                :class="optionClass('notion_blocks')"
                @click="selectOption('notion_blocks')"
              >
                <div class="flex items-start">
                  <div class="flex-grow">
                    <div class="font-medium text-gray-900">Notion blocks</div>
                    <div class="mt-1 text-sm text-gray-600">I want Kairo blocks in Notion to track things where I work.</div>
                  </div>
                  <div class="ml-4 pt-1">
                    <div class="h-4 w-4 rounded-full border" :class="radioClass('notion_blocks')" />
                  </div>
                </div>
              </button>

              <button
                type="button"
                class="w-full text-left rounded-lg border p-4 transition"
                :class="optionClass('kairo_board')"
                @click="selectOption('kairo_board')"
              >
                <div class="flex items-start">
                  <div class="flex-grow">
                    <div class="font-medium text-gray-900">Kairo Board</div>
                    <div class="mt-1 text-sm text-gray-600">I want a standalone space to track habits, time, and goals in one place.</div>
                  </div>
                  <div class="ml-4 pt-1">
                    <div class="h-4 w-4 rounded-full border" :class="radioClass('kairo_board')" />
                  </div>
                </div>
              </button>
            </div>

            <div class="mt-8">
              <h3 class="text-base sm:text-lg font-semibold text-gray-900">What do you want to get better at?</h3>
              <p class="mt-1 text-sm text-gray-600">Tell us in your own words.</p>

              <div class="mt-4">
                <textarea
                  v-model="hopesText"
                  rows="3"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm"
                  placeholder="I'm hoping Kairo will help me..."
                ></textarea>
              </div>
            </div>

            <div class="mt-8">
              <h3 class="text-base sm:text-lg font-semibold text-gray-900">If Kairo becomes valuable to you, what would you pay?</h3>
              <p class="mt-1 text-sm text-gray-600">This helps us choose fair pricing. Pick one.</p>

              <div class="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
                <button
                  v-for="opt in payOptions"
                  :key="opt.value"
                  type="button"
                  class="rounded-lg border px-3 py-2 text-sm font-medium transition"
                  :class="payOptionClass(opt.value)"
                  @click="selectPay(opt.value)"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <div v-if="error" class="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
              {{ error }}
            </div>

            <div class="mt-6 flex items-center justify-end">
              <button
                type="button"
                class="inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                :class="submitClass"
                :disabled="!canSubmit || saving"
                @click="submit"
              >
                <span v-if="saving">Saving...</span>
                <span v-else>Continue</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OnboardingUseCaseModal',
  props: {
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit'],
  data() {
    return {
      selected: null,
      hopesText: '',
      selectedPay: null,
      error: '',
    };
  },
  computed: {
    canSubmit() {
      return !!this.selected && this.hopesText.trim() && this.selectedPay !== null;
    },
    payOptions() {
      return [
        { label: 'Nothing', value: 0 },
        { label: '$1/mo', value: 1 },
        { label: '$2/mo', value: 2 },
        { label: '$3/mo', value: 3 },
        { label: '$5/mo', value: 5 },
      ];
    },
    submitClass() {
      const base = 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
      const disabled = 'bg-gray-300 cursor-not-allowed focus:ring-gray-300';
      return this.canSubmit && !this.saving ? base : disabled;
    },
  },
  methods: {
    selectOption(value) {
      this.selected = value;
      this.error = '';
    },
    optionClass(value) {
      return this.selected === value
        ? 'border-green-500 ring-2 ring-green-200 bg-green-50'
        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50';
    },
    radioClass(value) {
      return this.selected === value
        ? 'border-green-600 bg-green-600'
        : 'border-gray-300 bg-white';
    },
    selectPay(value) {
      this.selectedPay = value;
      this.error = '';
    },
    payOptionClass(value) {
      return this.selectedPay === value
        ? 'border-green-500 ring-2 ring-green-200 bg-green-50 text-gray-900'
        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700';
    },
    submit() {
      this.error = '';
      if (!this.selected || !this.hopesText.trim() || this.selectedPay === null) {
        this.error = 'Please answer all questions to continue.';
        return;
      }

      this.$emit('submit', {
        useCase: this.selected,
        hopes: this.hopesText.trim(),
        otherText: null,
        wouldPayUsdMonthly: this.selectedPay,
      });
    },
  },
};
</script>
